// src/components/BrokerCRM.js
import React, { useState } from "react";
import PropertyStep from "./steps/PropertyStep";
import ApplicantStep from "./steps/ApplicantStep";
import IncomeExpenses from "./steps/IncomeExpenses";
import AssetsLiabilitiesStep from "./steps/AssetsLiabilities";
import ExpensesStep from "./steps/ExpensesStep";
import NeedsObjectivesStep from "./steps/NeedsObjectives";
import BorrowingCapacityStep from "./steps/BorrowingCapacityStep";
import ReviewSubmitStep from "./steps/ReviewSubmit";
import SuggestedQuestions from "./steps/SuggestedQuestions";
import styles from "./BrokerCRM.module.css";

const sections = [
  { id: "property", label: "1. Property" },
  { id: "applicants", label: "2. Applicants" },
  { id: "income", label: "3. Income" },
  { id: "assets", label: "4. Assets & Liabilities" },
  { id: "expenses", label: "5. Expenses" },
  { id: "objectives", label: "6. Needs & Objectives" },
  { id: "capacity", label: "7. Borrowing Capacity" },
  { id: "review", label: "8. Review & Submit" },
];

function BrokerCRM() {
  const [currentSection, setCurrentSection] = useState("property");

  // ✅ Centralised global form state
  const [crmData, setCrmData] = useState({
    property: {},
    applicants: [],
    income: [],
    assetsLiabilities: {},
    expenses: {},
    objectives: {},
  });

  const [brokerNotes, setBrokerNotes] = useState("");

  // ✅ Safer updater – ensures nested updates don’t wipe other sections
  const updateCrmData = (section, data) => {
    setCrmData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data,
      },
    }));
  };

  const progress = Math.round(
    ((sections.findIndex((s) => s.id === currentSection) + 1) /
      sections.length) *
      100
  );

  return (
    <div className={styles.crmContainer}>
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <h2>Broker CRM</h2>
        <ul>
          {sections.map((section) => (
            <li
              key={section.id}
              className={currentSection === section.id ? styles.active : ""}
              onClick={() => setCurrentSection(section.id)}
            >
              {section.label}
            </li>
          ))}
        </ul>
      </div>

      {/* Main content */}
      <div className={styles.main}>
        <div className={styles.progressBar}>
          <strong>Application Progress</strong>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span>{progress}%</span>
        </div>

        <div className={styles.sectionContent}>
          {currentSection === "property" && (
            <PropertyStep
              data={crmData.property}
              updateCrmData={(data) => updateCrmData("property", data)}
            />
          )}
          {currentSection === "applicants" && (
            <ApplicantStep
              data={crmData.applicants}
              updateCrmData={(data) => updateCrmData("applicants", data)}
            />
          )}
          {currentSection === "income" && (
            <IncomeExpenses
              data={crmData.income}
              updateCrmData={(data) => updateCrmData("income", data)}
            />
          )}
          {currentSection === "assets" && (
            <AssetsLiabilitiesStep
              data={crmData.assetsLiabilities}
              updateCrmData={(data) => updateCrmData("assetsLiabilities", data)}
            />
          )}
          {currentSection === "expenses" && (
            <ExpensesStep
              data={crmData.expenses}
              updateCrmData={(data) => updateCrmData("expenses", data)}
            />
          )}
          {currentSection === "objectives" && (
            <NeedsObjectivesStep
              data={crmData.objectives}
              updateCrmData={(data) => updateCrmData("objectives", data)}
            />
          )}
          {currentSection === "capacity" && (
            <BorrowingCapacityStep crmData={crmData} />
          )}
          {currentSection === "review" && (
            <ReviewSubmitStep crmData={crmData} />
          )}
        </div>
      </div>

      {/* Right sidebar */}
      <div className={styles.rightSidebar}>
        <SuggestedQuestions step={currentSection} />
        <div className={styles.brokerNotes}>
          <h4>📝 Broker Notes</h4>
          <textarea
            value={brokerNotes}
            onChange={(e) => setBrokerNotes(e.target.value)}
            placeholder="Enter notes here..."
          />
        </div>
      </div>
    </div>
  );
}

export default BrokerCRM;
